import { introCue, introPositionMs } from '../game/intro-timeline';
import { GAME_HEIGHT, GAME_WIDTH, KoodooScene, MENU_TRACK } from './base-scene';

export { MENU_TRACK };

/**
 * How long the logo takes to settle after it appears.
 *
 * It must finish inside the 250ms between the reveal and the handoff: the main
 * menu redraws the same logo at the same position and at scale 1, so a punch
 * still mid-flight when the scene changes shows up as the title snapping size.
 */
const LOGO_PUNCH_MS = 200;

/**
 * The title sting: the logo lands on the hit, the menu opens on the drop.
 *
 * There is deliberately no skip — see docs/intro-sequence.md. The scene owns
 * no timing of its own either; it reads how far the theme has played and asks
 * `intro-timeline` what should be on screen.
 */
export class Intro extends KoodooScene {
  static readonly KEY = 'Intro';

  private air?: Phaser.GameObjects.Image;
  private airyObjects?: Phaser.GameObjects.Image;
  private logo?: Phaser.GameObjects.Image;
  private elapsedMs = 0;
  private handedOff = false;

  constructor() {
    super(Intro.KEY);
  }

  preload(): void {
    this.loadBackgrounds(['bg-1-basecolor', 'bg-2-air', 'bg-3-airyobjects']);
    this.load.image('main_logo', 'assets/images/main_logo.png');
  }

  create(): void {
    const { air, airyObjects } = this.addBackgrounds([
      'bg-1-basecolor',
      'bg-2-air',
      'bg-3-airyobjects',
    ]);
    this.air = air;
    this.airyObjects = airyObjects;

    this.setCursor('generic');

    this.elapsedMs = 0;
    this.logo = undefined;
    this.handedOff = false;

    // From the top. The sting is the sequence; skipping to the loop, as this
    // used to do, left the intro with nothing to be timed against.
    this.session.audio.playMusic(MENU_TRACK);
  }

  override update(_time: number, delta: number): void {
    if (this.air) {
      this.air.rotation += 0.0005;
    }
    if (this.airyObjects) {
      this.airyObjects.rotation -= 0.0005;
    }

    this.elapsedMs += delta;
    const cue = introCue(
      introPositionMs(this.session.audio.musicPositionMs(MENU_TRACK), this.elapsedMs),
    );

    if (cue.showLogo && !this.logo) {
      this.showLogo();
    }
    // `scene.start` is deferred to the next step, so update runs again first.
    if (cue.finished && !this.handedOff) {
      this.handedOff = true;
      this.scene.start('MainMenu');
    }
  }

  private showLogo(): void {
    this.logo = this.add
      .image(GAME_WIDTH / 2, GAME_HEIGHT / 4, 'main_logo')
      .setScale(1.35)
      .setAlpha(0);

    // A stamp, not a fade: the logo lands in the quiet and the drop follows it.
    this.tweens.add({ targets: this.logo, alpha: 1, duration: LOGO_PUNCH_MS / 2 });
    this.tweens.add({
      targets: this.logo,
      scale: 1,
      duration: LOGO_PUNCH_MS,
      ease: 'Back.easeOut',
    });
  }
}
